// Search-param validators for the planning routes. Kept apart from the pages so
// route files stay in the entry chunk without pulling the page modules.
import {
  isExceptionKind,
  isPlannedKind,
  isPlannedStatusFilter,
  type ExceptionKind,
  type PlannedOrderKind,
  type PlannedStatusFilter,
} from "./api";
import { planningExceptionListColumns, plannedOrderListColumns } from "@patronix/domain/list";
import { viewSearch, type ViewSearch } from "@/lib/listView";

export interface PlannedSearch extends ViewSearch {
  status: PlannedStatusFilter;
  kind?: PlannedOrderKind | undefined;
  q: string;
  po?: string | undefined;
  /** Mass release panel open. */
  mass?: true | undefined;
}

export function validatePlannedSearch(s: Record<string, unknown>): PlannedSearch {
  return {
    status: isPlannedStatusFilter(s.status) ? s.status : "open",
    ...(isPlannedKind(s.kind) ? { kind: s.kind } : {}),
    q: typeof s.q === "string" ? s.q : "",
    ...(typeof s.po === "string" && s.po ? { po: s.po } : {}),
    ...(s.mass === true || s.mass === "1" || s.mass === 1 ? { mass: true as const } : {}),
    ...viewSearch(plannedOrderListColumns, s),
  };
}

export interface ExceptionSearch extends ViewSearch {
  kind?: ExceptionKind | undefined;
}

export function validateExceptionSearch(s: Record<string, unknown>): ExceptionSearch {
  return { ...(isExceptionKind(s.kind) ? { kind: s.kind } : {}), ...viewSearch(planningExceptionListColumns, s) };
}

export interface CoverageSearch {
  item?: string | undefined;
  q?: string | undefined;
  /** Status filter, comma-separated. */
  status?: string | undefined;
  supplier?: string | undefined;
  buyer?: string | undefined;
  proc?: "purchase" | "manufacture" | undefined;
}

const UUIDISH = /^[0-9a-f-]{36}$/i;
const STATUSES = new Set(["critical", "no_stock", "warning", "watch", "overstock", "ok", "no_demand"]);

export function validateCoverageSearch(s: Record<string, unknown>): CoverageSearch {
  const out: CoverageSearch = {};
  if (typeof s.item === "string" && UUIDISH.test(s.item)) out.item = s.item;
  if (typeof s.q === "string" && s.q.trim()) out.q = s.q.slice(0, 40);
  if (typeof s.status === "string") {
    const st = s.status.split(",").filter((x) => STATUSES.has(x));
    if (st.length > 0) out.status = st.join(",");
  }
  if (typeof s.supplier === "string" && UUIDISH.test(s.supplier)) out.supplier = s.supplier;
  if (typeof s.buyer === "string" && UUIDISH.test(s.buyer)) out.buyer = s.buyer;
  if (s.proc === "purchase" || s.proc === "manufacture") out.proc = s.proc;
  return out;
}
